import type { EventSink, HitSource } from '../events'
import { activeRide, endRide, isBurrowed } from '../player/rides'
import { stunPlayer } from '../player/stun'
import { NO_PLAYER, NO_TICK } from '../world'
import type { World } from '../world'

/** Finished, respawning and invulnerable players cannot be hit. */
export function canBeHit(world: World, i: number): boolean {
  const p = world.players[i]!
  return p.placement === 0 && p.respawnAtTick === NO_TICK && p.invulnUntil <= world.tick
}

/**
 * The single damage path (ARCHITECTURE §2.4) for hazards, traps and projectiles. An active
 * shield absorbs the hit: it is cleared, a short invulnerability follows and `shieldPop` is
 * emitted. Otherwise the player tumbles (`stunPlayer`: no control, a small kick up, slow drift,
 * post-stun invulnerability) and `hit` is emitted. Returns false when the player could not be hit, so
 * the caller keeps its trap or projectile.
 *
 * Riders (D-050/D-056): hazards, traps and ride bumps never hit them; a burrowed rider is out of
 * reach of everything. A projectile breaks on a Crabwalker's shell (`shieldPop`, source `ride`)
 * and knocks any other rider off (the ride ends, then the hit lands as usual). `attacker` is who
 * caused it (item owner, rider), `NO_PLAYER` for track hazards.
 */
export function hit(
  world: World,
  i: number,
  source: HitSource,
  stunTicks: number,
  events: EventSink,
  attacker: number = NO_PLAYER,
  /** `skid` (Oil Slick): no control and slow for `stunTicks` instead of a tumble. */
  effect: 'stun' | 'skid' = 'stun',
): boolean {
  if (!canBeHit(world, i)) return false
  const p = world.players[i]!
  const tick = world.tick
  const ride = activeRide(world, p)
  if (ride) {
    if (isBurrowed(p, tick) || !isProjectile(source)) return false
    if (ride.blocksProjectiles) {
      events.push({ type: 'shieldPop', tick, player: i, source: 'ride', attacker })
      return true
    }
    endRide(world, i, events)
  }
  p.lastHitTick = tick
  if (p.shieldUntil > tick) {
    p.shieldUntil = 0
    p.invulnUntil = tick + world.cfg.items.shieldPopInvulnTicks
    events.push({ type: 'shieldPop', tick, player: i, source, attacker })
    return true
  }
  if (effect === 'skid') p.skidUntil = Math.max(p.skidUntil, tick + stunTicks)
  else stunPlayer(world, i, stunTicks, events)
  events.push({ type: 'hit', tick, player: i, source, stunTicks, attacker })
  return true
}

/** Spike balls, homing drones and the Leader Missile: the only things that reach a rider. */
function isProjectile(source: HitSource): boolean {
  return source === 'spike_ball' || source === 'homing_drone' || source === 'leader_missile'
}
